/**
 * A Discover section that loads its own list (#2303), the way a saved search
 * does (#2291): one request once the section comes within 200 px of the
 * viewport, another only on `retry` or a new `load`. A hidden section is not
 * drawn, so it asks nothing. An answer for an older `load` or attempt, or one
 * that comes after unmount, is dropped.
 *
 * `load` is the request: a new function (its section's count or viewer
 * changed) asks again, so a caller memoizes it on what it asks with. `null`
 * asks nothing yet (no viewer known), and the section stays loading.
 */
import { useCallback, useEffect, useState, type RefObject } from 'react';
import type { SectionStatus } from '../useDiscoverData';
import { useNearViewport } from '../saved/useNearViewport';

interface Answer<T> {
  load: () => Promise<T>;
  attempt: number;
  status: 'ready' | 'error';
  value?: T;
}

export interface NearLoad<T> {
  /** On the section's element: it loads once that comes near. */
  ref: RefObject<HTMLElement | null>;
  status: SectionStatus;
  /** The answer, while `status` is 'ready'. */
  value: T | undefined;
  retry: () => void;
}

export function useNearLoad<T>(load: (() => Promise<T>) | null): NearLoad<T> {
  const { ref, near } = useNearViewport<HTMLElement>('200px');
  const [attempt, setAttempt] = useState(0);
  const [answer, setAnswer] = useState<Answer<T> | null>(null);

  useEffect(() => {
    if (!near || !load) return;
    let alive = true;
    load().then(
      (value) => { if (alive) setAnswer({ load, attempt, status: 'ready', value }); },
      () => { if (alive) setAnswer({ load, attempt, status: 'error' }); },
    );
    return () => { alive = false; };
  }, [near, load, attempt]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  const current = answer?.load === load && answer.attempt === attempt ? answer : null;

  return { ref, status: current?.status ?? 'loading', value: current?.value, retry };
}
